import React, { useState, useEffect, useMemo } from 'react';
import { auth, db } from '../firebase';
import { signOut } from 'firebase/auth';
import { doc, getDoc, collection, addDoc, query, where, getDocs, updateDoc, increment } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import '../styles/Dashboard.css';
import OverviewSection from '../components/OverviewSection';
import TeamsSection from '../components/TeamsSection';
import EmployeeRequestsSection from '../components/EmployeeRequestsSection';
import EmployeesSection from '../components/EmployeesSection';
import AnalyticsSection from '../components/AnalyticsSection';
import ReportsSection from '../components/ReportsSection';
import BillingSection from '../components/BillingSection';
import SettingsSection from '../components/SettingsSection';
import FeedbackSection from '../components/FeedbackSection';
import OnboardingSection from '../components/OnboardingSection';
import { TeamModal } from '../components/Modals';
import { sortTeams, filterEmployees } from '../utils/dashboardHelpers';
import { checkAdminActionLimit } from '../utils/rateLimitHelper';
// ── SVG nav icons ──────────────────────────────────────────
const IconOverview = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
    <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
  </svg>
);
const IconTeams = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const IconEmployees = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const IconRequests = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10 9 9 9 8 9"/>
  </svg>
);
const IconAnalytics = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
);
const IconReports = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);
const IconBilling = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2"/>
    <line x1="2" y1="10" x2="22" y2="10"/>
  </svg>
);
const IconOnboarding = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 8 16 12 12 16"/>
    <line x1="8" y1="12" x2="16" y2="12"/>
  </svg>
);
const IconFeedback = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);
const IconSettings = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);
function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [teams, setTeams] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedEmployees, setApprovedEmployees] = useState([]);
  const [teamAverages, setTeamAverages] = useState([]);
  const [activeView, setActiveView] = useState('overview');
  const [newTeamName, setNewTeamName] = useState('');
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [expandedTeams, setExpandedTeams] = useState({});
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editingTeamName, setEditingTeamName] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingData, setBillingData] = useState(null);
  const sortedTeams = useMemo(() => sortTeams(teams), [teams]);
  const filteredEmployees = useMemo(() =>
    filterEmployees(approvedEmployees, employeeFilter, employeeSearch),
    [approvedEmployees, employeeFilter, employeeSearch]
  );
  useEffect(() => { loadDashboardData(); }, []);
  const loadDashboardData = async () => {
    try {
      const user = auth.currentUser;
      if (!user) { navigate('/login'); return; }
      if (!user.emailVerified) {
        alert('Please verify your email before accessing the dashboard.');
        await signOut(auth); navigate('/login'); return;
      }
      const adminDoc = await getDoc(doc(db, 'admins', user.uid));
      if (!adminDoc.exists()) {
        alert('Access denied. You do not have admin privileges.');
        await signOut(auth); navigate('/login'); return;
      }
      const adminData = adminDoc.data();
      const companyId = adminData.companyId;
      if (companyId === 'Null' || !companyId) { navigate('/company-setup'); return; }
      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      if (!companyDoc.exists()) {
        alert('Company data not found. Please contact support.');
        await signOut(auth); navigate('/login'); return;
      }
      setCompanyData({ id: companyId, ...companyDoc.data() });
      await Promise.all([
        loadTeams(companyId), loadPendingUsers(companyId),
        loadApprovedEmployees(companyId), loadTeamAverages(companyId),
        loadBillingData(companyId),
      ]);
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      alert('Failed to load dashboard. Please try again.');
      await signOut(auth); navigate('/login');
    }
  };
  const loadCompanyData = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      const adminDoc = await getDoc(doc(db, 'admins', user.uid));
      if (!adminDoc.exists()) return;
      const companyId = adminDoc.data().companyId;
      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      if (companyDoc.exists()) setCompanyData({ id: companyId, ...companyDoc.data() });
    } catch (error) { console.error('Error reloading company data:', error); }
  };
  const loadTeams = async (companyId) => {
    try {
      const snap = await getDocs(collection(db, `companies/${companyId}/teams`));
      setTeams(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); setTeams([]); }
  };
  const loadPendingUsers = async (companyId) => {
    try {
      const q = query(collection(db, 'users'), where('requestedCompanyId', '==', companyId), where('status', '==', 'pending'));
      const snap = await getDocs(q);
      setPendingUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); setPendingUsers([]); }
  };
  const loadApprovedEmployees = async (companyId) => {
    try {
      const q = query(collection(db, 'users'), where('companyId', '==', companyId), where('status', '==', 'approved'));
      const snap = await getDocs(q);
      setApprovedEmployees(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); setApprovedEmployees([]); }
  };
  const loadBillingData = async (companyId) => {
    try {
      const snap = await getDoc(doc(db, 'billingLimits', companyId));
      if (snap.exists()) setBillingData(snap.data());
    } catch (e) { console.error(e); }
  };
  const loadTeamAverages = async (companyId) => {
    try {
      const q = query(collection(db, 'teamAverages'), where('companyId', '==', companyId));
      const snap = await getDocs(q);
      setTeamAverages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); setTeamAverages([]); }
  };
  const handleLogout = async () => {
    try { await signOut(auth); navigate('/login'); }
    catch (e) { console.error(e); alert('Failed to logout.'); }
  };
  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim() || !companyData) return;
    try {
      await addDoc(collection(db, `companies/${companyData.id}/teams`), {
        name: newTeamName.trim().slice(0, 50), memberCount: 0, createdAt: new Date().toISOString()
      });
      setNewTeamName(''); setShowTeamModal(false);
      await loadTeams(companyData.id);
    } catch (e) { console.error(e); alert('Failed to create team.'); }
  };
  const handleEditTeamName = async (teamId) => {
    if (!editingTeamName.trim()) { setEditingTeamId(null); return; }
    const user = auth.currentUser;
    const limit = await checkAdminActionLimit(user.uid, 'teamNameChange');
    if (!limit.allowed) { alert(`Rate limit reached. Try again in a few minutes.`); return; }
    try {
      await updateDoc(doc(db, `companies/${companyData.id}/teams`, teamId), {
        name: editingTeamName.trim().slice(0, 50), updatedAt: new Date().toISOString()
      });
      setEditingTeamId(null); setEditingTeamName('');
      await loadTeams(companyData.id);
    } catch (e) { console.error(e); alert('Failed to update team name.'); }
  };
  const handleApproveUser = async (userId, teamId) => {
    if (!teamId) { alert('Please select a team'); return; }
    try {
      const functions = getFunctions();
      const approveTeamMember = httpsCallable(functions, 'approveTeamMember');
      await approveTeamMember({ userId, teamId });
      await loadPendingUsers(companyData.id);
      await loadApprovedEmployees(companyData.id);
      await loadTeams(companyData.id);
      alert('User approved successfully!');
    } catch (e) {
      console.error(e);
      if (e.code === 'functions/resource-exhausted') alert(e.message);
      else if (e.code === 'functions/failed-precondition') alert(e.message);
      else if (e.code === 'functions/permission-denied') alert('Permission denied.');
      else alert(`Failed to approve user: ${e.message}`);
    }
  };
  const handleRejectUser = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this request?')) return;
    try {
      await updateDoc(doc(db, 'users', userId), {
        status: 'rejected', teamId: null, companyId: null,
        requestedCompanyId: null, rejectedAt: new Date().toISOString(),
      });
      await loadPendingUsers(companyData.id);
    } catch (e) { console.error(e); alert('Failed to reject user.'); }
  };
  const handleMoveEmployee = async (userId, newTeamId) => {
    if (!newTeamId) { alert('Please select a team'); return; }
    const user = auth.currentUser;
    const limit = await checkAdminActionLimit(user.uid, 'userMove');
    if (!limit.allowed) { alert('Rate limit reached. Try again in a few minutes.'); return; }
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      const oldTeamId = userDoc.data()?.teamId;
      await updateDoc(doc(db, 'users', userId), { teamId: newTeamId, movedAt: new Date().toISOString() });
      if (oldTeamId) await updateDoc(doc(db, `companies/${companyData.id}/teams/${oldTeamId}`), { memberCount: increment(-1) });
      await updateDoc(doc(db, `companies/${companyData.id}/teams/${newTeamId}`), { memberCount: increment(1) });
      await loadApprovedEmployees(companyData.id);
      await loadTeams(companyData.id);
      alert('Employee moved successfully!');
    } catch (e) { console.error(e); alert('Failed to move employee.'); }
  };
  const handleRemoveEmployee = async (userId) => {
    const user = auth.currentUser;
    const limit = await checkAdminActionLimit(user.uid, 'userDeletion');
    if (!limit.allowed) { alert('Rate limit reached. Try again in a few minutes.'); return; }
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      const oldTeamId = userDoc.data()?.teamId;
      await updateDoc(doc(db, 'users', userId), {
        companyId: null, requestedCompanyId: null, teamId: null,
        status: 'pending', updatedAt: new Date().toISOString(),
      });
      if (oldTeamId) await updateDoc(doc(db, `companies/${companyData.id}/teams/${oldTeamId}`), { memberCount: increment(-1) });
      await loadApprovedEmployees(companyData.id);
      await loadTeams(companyData.id);
    } catch (e) { console.error(e); alert('Failed to remove employee.'); }
  };
  const toggleTeamExpanded = (teamId) => setExpandedTeams(prev => ({ ...prev, [teamId]: !prev[teamId] }));
  const expandAllTeams = () => { const e = {}; teams.forEach(t => { e[t.id] = true; }); setExpandedTeams(e); };
  const collapseAllTeams = () => setExpandedTeams({});
  const handleViewChange = (view) => { setActiveView(view); setMobileMenuOpen(false); };
  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }
  const navItems = [
    { id: 'overview',   label: 'Overview',              icon: <IconOverview /> },
    { id: 'teams',      label: 'Teams',                 icon: <IconTeams /> },
    { id: 'employees',  label: 'Employees',             icon: <IconEmployees /> },
    { id: 'requests',   label: 'Employee Requests',     icon: <IconRequests />, badge: pendingUsers.length },
    { id: 'analytics',  label: 'Analytics',             icon: <IconAnalytics /> },
    { id: 'reports',    label: 'Reports',               icon: <IconReports /> },
    { id: 'billing',    label: 'Payments & Billing',    icon: <IconBilling /> },
    { id: 'onboarding', label: 'Onboarding',            icon: <IconOnboarding /> },
    { id: 'feedback',   label: 'Report Bug & Feedback', icon: <IconFeedback /> },
    { id: 'settings',   label: 'Settings',              icon: <IconSettings /> },
  ];
  return (
    <div className="dashboard-page">
      <button
        className={`mobile-menu-toggle ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <span></span><span></span><span></span>
      </button>
      <div className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo-wrapper">
            <img src="/Zella logo green.png" alt="Zella" className="sidebar-logo-img" />
          </div>
          <p className="admin-email">{auth.currentUser?.email}</p>
        </div>
        <nav className="sidebar-nav">
          {navItems.map(({ id, label, icon, badge }) => (
            <a
              key={id}
              href="#"
              className={`nav-item ${activeView === id ? 'active' : ''}`}
              onClick={(e) => { e.preventDefault(); handleViewChange(id); }}
            >
              <span className="nav-icon">{icon}</span>
              {label}
              {badge > 0 && <span className="badge">{badge}</span>}
            </a>
          ))}
        </nav>
        <div className="sidebar-footer">
          <a href="/" className="btn-back-home">← Back to Website</a>
          <button className="btn-logout" onClick={handleLogout}>Log Out</button>
        </div>
      </div>
      {mobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}
      <div className="dashboard-main">
        {activeView === 'overview' && <OverviewSection companyData={companyData} teams={teams} approvedEmployees={approvedEmployees} pendingUsers={pendingUsers} teamAverages={teamAverages} billingOverLimit={!billingData?.active || teams.reduce((s,t) => s+(t.memberCount??0),0) > (billingData?.limit??10)} />}
        {activeView === 'teams' && <TeamsSection sortedTeams={sortedTeams} approvedEmployees={approvedEmployees} teamAverages={teamAverages} expandedTeams={expandedTeams} editingTeamId={editingTeamId} editingTeamName={editingTeamName} setEditingTeamId={setEditingTeamId} setEditingTeamName={setEditingTeamName} toggleTeamExpanded={toggleTeamExpanded} expandAllTeams={expandAllTeams} collapseAllTeams={collapseAllTeams} setShowTeamModal={setShowTeamModal} handleEditTeamName={handleEditTeamName} handleMoveEmployee={handleMoveEmployee} />}
        {activeView === 'requests' && (
          <EmployeeRequestsSection
            pendingUsers={pendingUsers}
            sortedTeams={sortedTeams}
            handleApproveUser={handleApproveUser}
            handleRejectUser={handleRejectUser}
            onRefresh={() => loadPendingUsers(companyData.id)}
          />
        )}
        {activeView === 'employees' && <EmployeesSection approvedEmployees={approvedEmployees} teams={teams} sortedTeams={sortedTeams} filteredEmployees={filteredEmployees} employeeFilter={employeeFilter} employeeSearch={employeeSearch} setEmployeeFilter={setEmployeeFilter} setEmployeeSearch={setEmployeeSearch} handleMoveEmployee={handleMoveEmployee} handleRemoveEmployee={handleRemoveEmployee} />}
        {activeView === 'analytics' && <AnalyticsSection teamAverages={teamAverages} sortedTeams={sortedTeams} companyData={companyData} approvedEmployees={approvedEmployees} billingOverLimit={!billingData?.active || teams.reduce((s,t) => s+(t.memberCount??0),0) > (billingData?.limit??10)} />}
        {activeView === 'reports' && <ReportsSection teamAverages={teamAverages} teams={sortedTeams} companyData={companyData} approvedEmployees={approvedEmployees} billingOverLimit={!billingData?.active || teams.reduce((s,t) => s+(t.memberCount??0),0) > (billingData?.limit??10)} />}
        {activeView === 'billing' && <BillingSection companyData={companyData} teams={teams} />}
        {activeView === 'onboarding' && <OnboardingSection companyData={companyData} />}
        {activeView === 'feedback' && <FeedbackSection />}
        {activeView === 'settings' && <SettingsSection companyData={companyData} onCompanyUpdate={loadCompanyData} />}
      </div>
      <TeamModal showTeamModal={showTeamModal} setShowTeamModal={setShowTeamModal} newTeamName={newTeamName} setNewTeamName={setNewTeamName} handleCreateTeam={handleCreateTeam} />
    </div>
  );
}
export default Dashboard;
